import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Check } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MerchDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const response = await axios.get(`${API_URL}/merch/${id}`);
        setItem(response.data);
        if (response.data.sizes?.length > 0) {
          setSelectedSize(response.data.sizes[0]);
        }
      } catch (error) {
        logger.error('Failed to fetch merch:', error);
        navigate('/merch');
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id, navigate]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    if (item.sizes?.length > 0 && !selectedSize) {
      toast.error('Please select a size');
      return;
    }
    
    setAddingToCart(true);
    try {
      await addToCart(item.id, 'merch', quantity, 0, selectedSize);
      toast.success('Added to cart');
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!item) return null;

  return (
    <div className="main-content pt-24" data-testid="merch-detail-page">
      <div className="container py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image */}
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden bg-white/5">
              <img 
                src={item.image_url} 
                alt={item.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Details */}
          <div>
            <p className="text-purple-400 font-medium mb-2 uppercase tracking-wider">
              {item.category}
            </p>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{item.name}</h1>
            <p className="text-gray-400 text-lg mb-6">{item.description}</p>
            
            <div className="text-3xl font-bold text-purple-400 mb-8">
              ${item.price.toFixed(2)}
            </div>

            {/* Size Selector */}
            {item.sizes && item.sizes.length > 0 && (
              <div className="mb-8">
                <p className="text-sm font-medium text-gray-400 mb-3">Size</p>
                <div className="flex flex-wrap gap-2">
                  {item.sizes.map(size => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`size-btn ${selectedSize === size ? 'selected' : ''}`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-8">
              <p className="text-sm font-medium text-gray-400 mb-3">Quantity</p>
              <div className="quantity-selector inline-flex">
                <button 
                  className="quantity-btn"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  data-testid="decrease-quantity"
                >
                  -
                </button>
                <span className="w-12 text-center font-medium">{quantity}</span>
                <button 
                  className="quantity-btn"
                  onClick={() => setQuantity(quantity + 1)}
                  data-testid="increase-quantity"
                >
                  +
                </button>
              </div>
            </div>

            {/* Add to Cart */}
            <button
              onClick={handleAddToCart}
              disabled={addingToCart || !item.in_stock}
              className={`w-full py-4 rounded-full font-medium flex items-center justify-center gap-2 transition-all ${
                item.in_stock 
                  ? 'btn-primary text-lg' 
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="add-to-cart-button"
            >
              {item.in_stock ? (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  Add to Cart - ${(item.price * quantity).toFixed(2)}
                </>
              ) : (
                'Sold Out'
              )}
            </button>

            {/* Features */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <h3 className="font-semibold mb-4">Details</h3>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  Premium quality materials
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  Limited edition design
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  Exclusive @DaRealQJones branding
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  Free shipping on orders over $100
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
